import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { auth } from './firebase';

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [status, setStatus] = useState('checking');   // 'checking' | 'success' | 'error'
  const [countdown, setCountdown] = useState(5);

  const [userReady, setUserReady] = useState(false);
  const [user, setUser] = useState(null);

  // 1) Wait for Firebase auth to resolve
  useEffect(() => {
    const unsub = auth.onAuthStateChanged(u => {
      setUser(u);
      setUserReady(true);
    });
    return () => unsub();
  }, []);

  // 2) When auth is ready, run payment check
  useEffect(() => {
    if (!userReady) return;

    const orderId = searchParams.get('order_id');
    console.log('✅ PaymentSuccess mounted with orderId=', orderId);

    if (!orderId) {
      console.error('❌ No orderId in URL');
      setStatus('error');
      return;
    }

    if (!user) {
      console.error('❌ No user logged in (after auth init)');
      setStatus('error');
      return;
    }

    checkPayment(user, orderId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userReady, user]);

const checkPayment = async (user, orderId) => {
    try {
      console.log('📋 Verifying payment for Order ID:', orderId);
      
      // 🚀 1. GET THE FIREBASE TOKEN
      const token = await user.getIdToken();

      const verifyResponse = await fetch(
        'https://payment-server-8ima.onrender.com/api/verify-flitt-payment',
        {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            // 🚀 2. SEND THE TOKEN IN THE HEADERS
            'Authorization': `Bearer ${token}` 
          },
          body: JSON.stringify({
            orderId,
            userId: user.uid,
          }),
        }
      );

      const verifyData = await verifyResponse.json();
      console.log('Verification response:', verifyData);

      if (!verifyData.success) {
        console.error('❌ Payment verification failed:', verifyData.error || verifyData.message);
        setStatus('error');
        return;
      }

      // If successful, refresh the user token so the UI updates immediately
      try {
        console.log('🔄 Refreshing Firebase token...');
        await user.getIdTokenResult(true);
        await new Promise(resolve => setTimeout(resolve, 1000));
        console.log('✅ User data refreshed, payment verified');
      } catch (e) {
        console.warn('⚠️ Could not refresh user token, but payment verified:', e);
      }

      setStatus('success');
    } catch (error) {
      console.error('❌ Error in checkPayment:', error);
      setStatus('error');
    }
  };


  // 3) Auto-redirect after success
  useEffect(() => {
    if (status !== 'success') return;

    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          localStorage.removeItem('selectedPlan');
          localStorage.removeItem('lastFlittOrderId');
          localStorage.removeItem('sessionId');
          navigate('/dashboard');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [status, navigate]);

  // === UI STATES ===

  if (status === 'checking') {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(130deg, #f8fafc 67%, #edf1f6 100%)',
        }}
      >
        <div
          style={{
            background: '#fff',
            borderRadius: 16,
            padding: '60px 40px',
            boxShadow: '0 8px 32px #b3def233',
            textAlign: 'center',
            maxWidth: 400,
          }}
        >
          <div style={{ fontSize: 48, marginBottom: 20, animation: 'spin 2s linear infinite' }}>
            ⏳
          </div>
          <h2 style={{ color: '#39AED9', marginBottom: 10 }}>Processing...</h2>
          <p style={{ color: '#666', fontSize: 16 }}>
            Verifying your payment and activating subscription...
          </p>
        </div>
        <style>{`
          @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        `}</style>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(130deg, #f8fafc 67%, #edf1f6 100%)',
        }}
      >
        <div
          style={{
            background: '#fff',
            borderRadius: 16,
            padding: '60px 40px',
            boxShadow: '0 8px 32px #b3def233',
            textAlign: 'center',
            maxWidth: 400,
          }}
        >
          <div style={{ fontSize: 48, marginBottom: 20 }}>❌</div>
          <h2 style={{ color: '#c92a2a', marginBottom: 10 }}>Payment Verification Failed</h2>
          <p style={{ color: '#666', fontSize: 16, marginBottom: 20 }}>
            We couldn't verify your payment. Please try again or contact support.
          </p>
          <button
            onClick={() => navigate('/pricing')}
            style={{
              background: '#39AED9',
              color: '#fff',
              border: 'none',
              padding: '12px 24px',
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Back to Pricing
          </button>
        </div>
      </div>
    );
  }

  // status === 'success'
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(130deg, #f8fafc 67%, #edf1f6 100%)',
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 16,
          padding: '60px 40px',
          boxShadow: '0 8px 32px #b3def233',
          textAlign: 'center',
          maxWidth: 400,
        }}
      >
        <div style={{ fontSize: 48, marginBottom: 20 }}>✅</div>
        <h2 style={{ color: '#37b24d', marginBottom: 10 }}>Payment Successful!</h2>
        <p style={{ color: '#666', fontSize: 16, marginBottom: 20 }}>
          Your subscription is now active. Redirecting in {countdown}s...
        </p>
        <button
          onClick={() => {
            localStorage.removeItem('selectedPlan');
            localStorage.removeItem('lastFlittOrderId');
            localStorage.removeItem('sessionId');
            navigate('/dashboard');
          }}
          style={{
            background: '#39AED9',
            color: '#fff',
            border: 'none',
            padding: '12px 24px',
            borderRadius: 8,
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Go to Dashboard
        </button>
      </div>
    </div>
  );
};

export default PaymentSuccess;
